import { Component, signal, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';

import { HttpClient, HttpHeaders } from '@angular/common/http';

import { AngularHtmlQrcodeService } from 'angular-html5-qrcode';
import { Html5Qrcode, Html5QrcodeSupportedFormats, Html5QrcodeScannerState } from 'html5-qrcode';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule
  ],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App {
    protected readonly title = signal('dswd-verify');

    Html5QrcodeSupportedFormats = Html5QrcodeSupportedFormats;
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
    html5QrCode!: Html5Qrcode;

    links = {
      government: [
        {name: 'Office of the President', link: 'https://op-proper.gov.ph/'},
        {name: 'Office of the Vice President', link: 'https://www.ovp.gov.ph/'},
        {name: 'Senate of the Philippines', link: 'https://web.senate.gov.ph/'},
        {name: 'House of Representatives', link: 'https://www.congress.gov.ph/'},
        {name: 'Supreme Court', link: 'https://sc.judiciary.gov.ph/'},
        {name: 'Sandiganbayan', link: 'https://sb.judiciary.gov.ph/'}
      ],
      other: [
        {name: 'Data Privacy', link: ''},
        {name: 'Citizen\'s Charter', link: ''},
        {name: 'Freedom of Information (FOI)', link: ''},
        {name: 'Transparecy Seal', link: ''}
      ]
    }

    qrcodeValue: any = {};
    scannerEnabled: boolean = false;
    allowScaning: boolean = true;
    private cameraBusy = false; // guards start/stop against re-entry mid-transition

    profileModal: boolean = false;
    profile_modal_data: {
      img: string;
      id: string;
      lastname: string;
      firstname: string;
      middlename: string | null;
      suffix: string | null;
      verified: boolean;
      date_issued: string;
      benefits: string[];
    } = this.emptyProfileModalData();

    private emptyProfileModalData() {
      return {
        img: '',
        id: '',
        lastname: '',
        firstname: '',
        middlename: null,
        suffix: null,
        verified: false,
        date_issued: '',
        benefits: [] as string[],
      };
    }

    loading: boolean = false;
    loading_message: string = '';

    alert: boolean = false;
    alert_type: string = '';
    alert_message: string = '';

    constructor(
      private qrService: AngularHtmlQrcodeService,
      private http: HttpClient
    ) {}

    ngOnInit() {
      this.html5QrCode = new Html5Qrcode("reader");
    }

    private isIOS(): boolean {
      const ua = navigator.userAgent || '';
      // Modern iPadOS reports as Macintosh in UA — touch points disambiguate.
      const touchMac = /Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1;
      return /iPad|iPhone|iPod/.test(ua) || touchMac;
    }

    // html5-qrcode rejects/throws raw strings, not Error objects, so anything
    // reading `err.message` alone misses them.
    private errorText(err: any): string {
      return typeof err === 'string' ? err : (err?.message ?? '');
    }

    /**
     * The library's StateManagerImpl throws this when a previous transition was
     * started but never executed or cancelled. Its own `stop()` leaks exactly
     * that: it calls startTransition(NOT_STARTED) and only executes the
     * transaction inside `renderedCamera.close().then(...)` — with no `.catch`.
     * If closing the camera rejects, the transaction is stranded and every
     * later start() throws, permanently, until the instance is replaced.
     */
    private isTransitionError(err: any): boolean {
      return this.errorText(err).includes('transition');
    }

    /**
     * Recover from a stranded transaction without making the user reload.
     * A fresh Html5Qrcode gets a fresh StateManagerImpl; we also stop any live
     * MediaStream tracks first so the camera hardware/indicator is released
     * even though the library's own close() failed.
     */
    private recreateScanner() {
      const container = document.getElementById('reader');
      const video = container?.querySelector('video') as HTMLVideoElement | null;
      const stream = video?.srcObject as MediaStream | null;
      stream?.getTracks().forEach(track => track.stop());

      if (container) {
        container.innerHTML = '';
      }
      this.html5QrCode = new Html5Qrcode('reader');
      this.scannerEnabled = false;
    }

    private cameraErrorMessage(err: any): string {
      if (this.isTransitionError(err)) {
        return 'Camera was still shutting down. Please tap Scan again.';
      }
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        return 'Camera permission denied. Enable camera access in your browser settings, then reload.';
      }
      if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
        return 'No camera found on this device.';
      }
      if (name === 'NotReadableError' || name === 'TrackStartError') {
        return 'Camera is in use by another app. Close other apps and try again.';
      }
      if (name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError') {
        return 'This camera does not support the requested settings.';
      }
      if (name === 'SecurityError') {
        return 'Camera blocked. Please open this site over HTTPS.';
      }
      return 'Unable to start camera. Please check permissions and try again.';
    }

    async startCamera() {
      // Ignore taps while a previous start/stop is still transitioning — calling
      // start()/stop() mid-transition throws "Cannot transition to a new state".
      if (this.cameraBusy) {
        return;
      }
      if (this.scannerEnabled) {
        await this.stopCamera();
        return;
      }

      // iOS Safari (and most browsers) only allow getUserMedia on HTTPS.
      if (!window.isSecureContext) {
        this.showAlert('Camera requires a secure (HTTPS) connection.', 'error');
        return;
      }

      // Older iOS Safari builds don't expose mediaDevices.getUserMedia.
      if (!navigator.mediaDevices?.getUserMedia) {
        this.showAlert('Camera is not supported on this browser. Try Safari/Chrome on a recent OS.', 'error');
        return;
      }

      const container = document.getElementById('reader');
      if (!container) {
        console.warn("QR container not found. Make sure #reader exists in the DOM.");
        return;
      }

      const qrboxFunction = () => {
        const width = container.offsetWidth || window.innerWidth;
        const height = container.offsetHeight || window.innerHeight;
        const size = Math.min(width, height) * 0.8;
        return { width: size, height: size };
      };

      const config = { fps: 5, qrbox: qrboxFunction, aspectRatio: 1.0 };

      const onScan = (decodedText: string) => {
        if (this.allowScaning) {
          this.decodeQr(decodedText);
          this.showLoading('Verifying ID. Please wait...');
          this.allowScaning = false;
          this.verifyQrCode(decodedText);
        }
      };
      const onErr = (_: any) => { /* per-frame noise — ignore */ };

      // html5-qrcode only accepts facingMode as a plain string or `{ exact }` —
      // `{ ideal: ... }` is rejected ("'facingMode' should be string or object
      // with exact as key"). A plain string is advisory at the getUserMedia level:
      // it prefers the rear camera and falls back to any camera on its own, so we
      // don't need (and must not chain) a second start() — that second call mid-
      // transition is what threw "Cannot transition to a new state".
      this.cameraBusy = true;
      // await inside try/finally, NOT a .then().catch().finally() chain:
      // Html5Qrcode.start() is a plain function that throws SYNCHRONOUSLY on
      // several paths (missing args, and startTransition when a previous
      // transaction is stranded). A synchronous throw never builds the promise
      // chain, so .finally() never runs and cameraBusy stays true forever —
      // after which both startCamera() and stopCamera() early-return at their
      // guards and the scanner is dead until a page reload.
      try {
        await this.html5QrCode.start({ facingMode: 'environment' }, config, onScan, onErr);

        this.scannerEnabled = true;
        const video = container.querySelector('video');
        if (video) {
          // Required for iOS Safari to play inline instead of fullscreen.
          video.setAttribute('playsinline', '');
          video.setAttribute('webkit-playsinline', '');
          video.muted = true;
          if (this.isIOS()) {
            video.style.width = '100%';
            video.style.height = '100%';
            video.style.objectFit = 'cover';
            video.style.transform = 'rotate(0deg)';
          }
        }
      } catch (err) {
        console.error('Camera start failed', err);
        this.scannerEnabled = false;
        if (this.isTransitionError(err)) {
          // Stranded transaction — swap in a clean instance so the next tap works.
          this.recreateScanner();
        }
        this.showAlert(this.cameraErrorMessage(err), 'error');
      } finally {
        this.cameraBusy = false;
      }
    }

    async stopCamera() {
      if (this.cameraBusy) {
        return;
      }
      this.cameraBusy = true;
      try {
        // Ask the library, not our own scannerEnabled flag, which can drift out
        // of sync with it. stop() throws synchronously when it isn't running.
        if (this.html5QrCode.getState() !== Html5QrcodeScannerState.NOT_STARTED) {
          await this.html5QrCode.stop();
        }
      } catch (err) {
        console.error('Stop error', err);
        if (this.isTransitionError(err)) {
          this.recreateScanner();
        }
      } finally {
        this.scannerEnabled = false;
        this.cameraBusy = false;
      }
    }

    triggerUpload() {
      // Clear the previous file so picking the SAME file again re-fires the
      // `change` event. Browsers skip the event when the value is unchanged.
      this.fileInput.nativeElement.value = '';
      this.fileInput.nativeElement.click();
    }

    handleFile(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      // Reset right away so the next pick — even of this same file — re-fires.
      input.value = '';
      if (!file) return;

      // iPhone default photo format is HEIC, which browser canvas APIs can't
      // decode for QR scanning. Catch it early with a clear instruction.
      const isHeic = /\.(heic|heif)$/i.test(file.name) || /heic|heif/i.test(file.type);
      if (isHeic) {
        this.showAlert(
          'HEIC images are not supported. On iPhone: Settings → Camera → Formats → Most Compatible, or convert to JPG/PNG first.',
          'error'
        );
        return;
      }

      this.html5QrCode.scanFile(file, false)
      .then(decodedText => {
        if (this.allowScaning) {
          this.decodeQr(decodedText);

          this.showLoading('Verifying ID. Please wait...');

          this.allowScaning = false;

          this.verifyQrCode(decodedText);
        }
      })
      .catch(_ => this.showAlert('Unable to find a QR code in this image. Try a clearer photo.', 'error'));
    }

    verifyQrCode (qrData: string) {
        // Always start from a clean slate so values from the previous scan
        // (middlename, suffix, benefits, etc.) don't leak into this modal.
        this.profile_modal_data = this.emptyProfileModalData();

        const url = `${environment.apiUrl}/api/v1/public/qrcode/verify`;
        const headers = new HttpHeaders()
                          .set('Content-Type', 'application/json')
                          .set('Accept', 'application/json');

        this.http.post(url, qrData, { headers: headers }).subscribe({
            next: (response: any) => { console.log('response', response);
                this.profile_modal_data.img = response.data.profilePicture;
                this.profile_modal_data.id = response.data.PWDN || response.data.UDI || null;
                this.profile_modal_data.lastname = response.data.lastName;
                this.profile_modal_data.firstname = response.data.firstName;

                this.profile_modal_data.middlename = (response.data.middleName && response.data.middleName != '')
                  ? response.data.middleName
                  : null;

                this.profile_modal_data.suffix = (response.data.suffix && response.data.suffix != '')
                  ? response.data.suffix
                  : null;

                this.profile_modal_data.benefits = Array.isArray(response.data.benefits)
                  ? response.data.benefits.filter((b: any) => typeof b === 'string' && b.length > 0)
                  : [];

                this.profile_modal_data.verified = true;

                const dateIssued = new Date(response.data.dateIssued);

                this.profile_modal_data.date_issued = dateIssued.toLocaleDateString('en-US', {
                  month: 'long', day: 'numeric', year: 'numeric'
                });

                // Hide the loading overlay before opening the success modal,
                // otherwise both stay layered on top of each other.
                this.hideLoading();
                this.showSuccesScan();
            },
            error: (err) => {
                this.hideLoading();
                // A genuine credential that fails an issuance requirement — not a
                // forgery. Say which, or the holder is told their valid ID is fake.
                if (err?.error?.code === 'SIGNATURE_REQUIRED') {
                    this.showAlert(
                        err?.error?.message
                          ?? 'This Solo Parent ID is missing the required signature and cannot be verified.',
                        'error'
                    );
                    return;
                }
                this.showAlert('This ID is either invalid or not verified.', 'error')
            }
        });
    }

    decodeQr(qrCode: string|null): void {
      this.qrcodeValue = qrCode ? JSON.parse(qrCode) : {};
      console.log('qrcode value', this.qrcodeValue);
    }

    showSuccesScan() {
      this.profileModal = true;
    }

    hideSuccessScan() {
      this.profileModal = false;
      // Reset the data too so a stale modal doesn't flash through behind a
      // future scan before the new payload arrives.
      this.profile_modal_data = this.emptyProfileModalData();

      this.allowScaning = true;
    }

    showLoading(message: string) {
      this.loading_message = message;
      this.loading = true;
    }

    hideLoading() {
      this.loading_message = '';
      this.loading = false;
    }

    showAlert(message: string, type: string = 'success') {
      this.alert_message = message;
      this.alert_type = type;
      this.alert = true;
    }

    hideAlert() {
      this.alert_message = '';
      this.alert_type = '';
      this.alert = false;

      this.allowScaning = true;
    }
}
